import { Box } from '@mui/material';
import { ISelectedDate, PeriodEnum } from 'common/defines/clients';
import { QUERY_KEY } from 'constants/constants';
import { DATE_STANDARD_FORMAT } from 'constants/date';
import { ILooseFruitData, IYieldSummary } from 'interfaces/erms';
import moment from 'moment';
import { useState } from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { sendGetLocationDetails } from 'services/clients/apiClient.services';
import { getLooseFruitChartData, getYieldChart } from 'services/erms';
import { useAppSelector } from 'store/hooks';
import { mapViewSelector } from 'store/slices/mapViewSlice';
import FilterDate from '../components/FilterDate';
import ComparisonSection from './ComparisonSection';
import FruitGradeBox from './FruitGradeBox';
import LooseFruitBox from './LooseFruitBox';
import YieldBox from './YieldBox';

const HarvestModule = () => {
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodEnum>(PeriodEnum.ONE_MONTH);
  const [selectedDate, setSelectedDate] = useState<ISelectedDate>({
    startDate: moment().subtract(1, 'month').toISOString(),
    endDate: moment().toISOString(),
  });
  const [yieldData, setYieldData] = useState<IYieldSummary[]>([]);
  const [looseFruitData, setLooseFruitData] = useState<ILooseFruitData[]>([]);
  const [hoverDatePoint, setHoverDatePoint] = useState<string>('');
  const { endDate, startDate } = selectedDate;

  const { levelId, cropType } = useAppSelector(mapViewSelector);
  const { clientId } = useParams();

  useQuery(
    [QUERY_KEY.GET_YIELD_DATA, levelId, startDate, endDate],
    () => getYieldChart({ levelId: levelId!, fromDate: startDate, toDate: endDate }),
    {
      enabled: !!levelId,
      onSuccess(data: IYieldSummary[]) {
        const sortData = [...data].sort(
          (a, b) => moment(a._id, DATE_STANDARD_FORMAT).valueOf() - moment(b._id, DATE_STANDARD_FORMAT).valueOf()
        );
        setYieldData(sortData);
        const firstData = sortData[0];
        setHoverDatePoint(firstData._id);
      },
    }
  );

  const { data: hierarchyStructure } = useQuery(
    [`${QUERY_KEY.DATA_MAP_KEY}_CLIENT`, levelId],
    () => sendGetLocationDetails(levelId || 'null'),
    {
      enabled: !!clientId && !!cropType && !!levelId,
      keepPreviousData: false,
    }
  );

  const area = hierarchyStructure?.data?.area || 0;
  const totalTree = hierarchyStructure?.data?.totalTree || 0;

  useQuery(
    [QUERY_KEY.GET_LOOSE_FRUIT_DATA, levelId, startDate, endDate],
    () => getLooseFruitChartData({ levelId: levelId!, fromDate: startDate, toDate: endDate }),
    {
      enabled: !!levelId,
      onSuccess(data: ILooseFruitData[]) {
        const sortData = [...data].sort(
          (a, b) => moment(a._id, DATE_STANDARD_FORMAT).valueOf() - moment(b._id, DATE_STANDARD_FORMAT).valueOf()
        );
        setLooseFruitData(sortData);
      },
    }
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <FilterDate
        selectedPeriod={selectedPeriod}
        setSelectedPeriod={setSelectedPeriod}
        setSelectedDate={setSelectedDate}
      />
      <YieldBox setHoverDatePoint={setHoverDatePoint} yieldChartData={yieldData} area={area} totalTree={totalTree} />
      <FruitGradeBox hoverDatePoint={hoverDatePoint} />
      <LooseFruitBox
        looseFruitData={looseFruitData}
        area={area}
        totalTree={totalTree}
        hoverDatePoint={hoverDatePoint}
      />
      <ComparisonSection
        yieldData={yieldData}
        hoverDatePoint={hoverDatePoint}
        selectedPeriod={selectedPeriod}
        area={area}
        totalTree={totalTree}
        looseFruitData={looseFruitData}
      />
    </Box>
  );
};

export default HarvestModule;
